import { FormControl, IconButton } from "@chakra-ui/react";
import { Select } from "chakra-react-select";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { getAxiosInstance } from "../utils/axiosInstance";
import { getRandomColor } from "../utils/getRandomColor";
import { MdClose } from "react-icons/md";

interface ICategorySelect {
    mainCategory?: string;
    onCategorySelect: (categoryId?: string) => void;
    subCategory: string[];
    onSubCategorySelect: (categoryId: string[]) => void;
    isRequired?: boolean;
    customClass?: string;
    hideReset?: boolean;
}

const CategorySelect = (props: ICategorySelect) => {
    const {
        mainCategory,
        onCategorySelect,
        subCategory,
        onSubCategorySelect,
        isRequired
    } = props;

    const { t } = useTranslation("translations");

    const [categoryList, setCategoryList] = useState<{ value: string, label: string, colorScheme: string }[]>([]);
    const [subcategoryList, setSubcategoryList] = useState<{ value: string, label: string, colorScheme: string }[]>([]);

    const fetchCategories = async () => {
        try {
            const res = await getAxiosInstance().get("Categories");

            if (res.data) {
                setCategoryList(res.data.map((x: any) => ({ value: x.id, label: x.name, colorScheme: getRandomColor() })))
            } else {
                setCategoryList([]);
            }
        } catch (e) {
            toast.error(t("ERROR_FETCHING_CATEGORY_LIST"), { position: "bottom-right" });
        }
    }

    const fetchSubCategories = async () => {
        try {
            const res = await getAxiosInstance().get(`Categories/${mainCategory}`);

            if (res.data) {
                const tmp = [];

                for (const x of res.data.subCategories) {
                    tmp.push({ value: x.id, label: x.name, colorScheme: getRandomColor() });

                    for (const y of x.subCategories) {
                        tmp.push({ value: y.id, label: " • " + y.name + ` (${x.name})`, colorScheme: getRandomColor() });
                    }
                }

                setSubcategoryList(tmp);
            } else {
                setSubcategoryList([]);
            }
        } catch (e) {
            toast.error(t("ERROR_FETCHING_CATEGORY_LIST"), { position: "bottom-right" });
        }
    }

    useEffect(() => {
        fetchCategories().then();
    }, [])

    useEffect(() => {
        if (mainCategory)
            fetchSubCategories().then();
    }, [mainCategory])

    return (
        <>
            <FormControl className={props.customClass ?? "mx-2 my-2 md:my-0"}>
                <Select
                    isRequired={isRequired}
                    name={t("SELECT_MAIN_CATEGORY")}
                    options={categoryList}
                    placeholder={t("SELECT_MAIN_CATEGORY")}
                    value={mainCategory && categoryList.findIndex((x) => x.value === mainCategory) != -1 ?
                        categoryList[categoryList.findIndex((x) => x.value === mainCategory)] :
                        []
                    }
                    onChange={(e: any) => {
                        if (e) {
                            onCategorySelect(e.value);
                        }
                    }}
                />
            </FormControl>
            <FormControl className={props.customClass ?? "mx-2 my-2 md:my-0"}>
                <Select
                    isMulti={true}
                    name={t("SELECT_SUBCATEGORY")}
                    options={subcategoryList}
                    placeholder={t("SELECT_SUBCATEGORY")}
                    value={subCategory ? subCategory.map((x) => subcategoryList[subcategoryList.findIndex((y) => y.value === x)]) : []}
                    onChange={(e) => {
                        if (e) {
                            const tmp: string[] = [];
                            for (const x of e.values()) {
                                tmp.push(x.value);
                            }

                            onSubCategorySelect(tmp);
                        }
                    }}
                />
            </FormControl>
            {
                props.hideReset &&
                <IconButton
                    colorScheme={"white"}
                    aria-label='Action'
                    fontSize='30px'
                    rounded={15}
                    icon={<MdClose color={"red"} />}
                    onClick={() => {
                        onCategorySelect(undefined);
                        onSubCategorySelect([]);
                    }}
                />
            }
        </>
    )
}

export default CategorySelect;